import type { Agent } from "../types";

/**
 * Get avatar gender prefix based on voice gender
 */
function getAvatarPrefix(voiceGender?: string): "m" | "f" {
  if (!voiceGender) {
    return Math.random() > 0.5 ? "f" : "m";
  }
  
  const lowerGender = voiceGender.toLowerCase();
  if (lowerGender === "male" || lowerGender === "m") {
    return "m";
  } else if (lowerGender === "female" || lowerGender === "f") {
    return "f";
  }
  
  // Random if gender not recognized
  return Math.random() > 0.5 ? "f" : "m";
}

/**
 * Generate all available avatar IDs for a given prefix
 */
function getAllAvatarsForPrefix(prefix: "m" | "f"): string[] {
  return Array.from({ length: 10 }, (_, i) => {
    const num = String(i + 1).padStart(2, "0");
    return `${prefix}${num}`;
  });
}

/**
 * Select a random avatar ID based on voice gender and optionally avoiding already-taken avatars
 * 
 * @param voiceGender - The gender of the voice (male/female/m/f) or undefined for random
 * @param existingAgents - Optional list of existing agents to avoid avatar collisions
 * @returns A random avatar ID (e.g., "m01", "f05")
 */
export function selectRandomAvatar(
  voiceGender?: string,
  existingAgents?: Agent[]
): string {
  const prefix = getAvatarPrefix(voiceGender);
  const allAvatars = getAllAvatarsForPrefix(prefix);
  
  // If no existing agents provided, pick from all available
  if (!existingAgents || existingAgents.length === 0) {
    const randomIndex = Math.floor(Math.random() * allAvatars.length);
    return allAvatars[randomIndex];
  }
  
  // Filter out already-taken avatars
  const takenAvatars = new Set(
    existingAgents.map(agent => agent.avatar_id).filter(Boolean)
  );
  const availableAvatars = allAvatars.filter(
    avatarId => !takenAvatars.has(avatarId)
  );
  
  // If all avatars are taken, just pick randomly from all (collision is ok)
  if (availableAvatars.length === 0) {
    const randomIndex = Math.floor(Math.random() * allAvatars.length);
    return allAvatars[randomIndex];
  }
  
  // Pick randomly from available avatars
  const randomIndex = Math.floor(Math.random() * availableAvatars.length);
  return availableAvatars[randomIndex];
}
